import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { createUser } from '../services/users';
import { createPatientAdmin } from '../services/patients';
import { listLocations } from '../services/locations';
import { listSharedUsers } from '../services/sharedUsers';
import { passwordPolicyMessage, validatePasswordPolicy } from '../utils/passwordPolicy';

const primaryButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#3D3D3D',
        boxShadow: 'none'
    }
};

const secondaryButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const toApiDate = (date) => {
    if (!date) {
        return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }

    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (!match) {
        return undefined;
    }

    return `${match[3]}-${match[2]}-${match[1]}`;
};

const toPickerValue = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const calculateAgeFromDate = (date) => {
    const normalized = toApiDate(date) || date;
    if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        return '';
    }

    const birthDate = new Date(`${normalized}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) {
        return '';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
    }

    return age >= 0 ? String(age) : '';
};

const createEmptyReferenceContact = () => ({
    name: '',
    kinship: '',
    age: '',
    phone: ''
});

const createEmptyEquipmentItem = () => ({
    type: 'Tablet',
    details: ''
});

const normalizeEquipmentItems = (items = []) => {
    if (!Array.isArray(items)) {
        return [createEmptyEquipmentItem()];
    }

    const normalized = items
        .map((item) => ({
            type: String(item?.type || '').trim() || 'Tablet',
            details: String(item?.details || item?.notes || item?.identifier || '').trim()
        }))
        .filter((item) => item.type || item.details);

    return normalized.length > 0 ? normalized : [createEmptyEquipmentItem()];
};

const getDefaultPatientForm = () => ({
    uuid: '',
    name: '',
    gender: '',
    age: '',
    birthDate: '',
    street: '',
    zip: '',
    parish: '',
    municipality: '',
    idCard: '',
    nif: '',
    healthNumber: '',
    niss: '',
    maritalStatus: '',
    education: '',
    professionalStatus: '',
    disability: ''
});

const toApiGender = (value) => {
    const normalized = (value || '').trim().toUpperCase();
    if (!normalized) return undefined;

    const map = {
        WOMAN: 'WOMAN',
        FEMALE: 'WOMAN',
        FEMININO: 'WOMAN',
        MULHER: 'WOMAN',
        MAN: 'MAN',
        MALE: 'MAN',
        MASCULINO: 'MAN',
        HOMEM: 'MAN',
        TRANS_WOMAN: 'TRANS_WOMAN',
        TRANS_MAN: 'TRANS_MAN',
        NONBINARY: 'NONBINARY',
        NAO_BINARIO: 'NONBINARY',
        AGENDER: 'AGENDER',
        GENDERQUEER: 'GENDERQUEER',
        PREFER_NO_ANSWER: 'PREFER_NO_ANSWER',
        NOT_LISTED: 'NOT_LISTED',
        UNKNOWN: 'UNKNOWN'
    };

    return map[normalized];
};

const subordinateUsernamePattern = /^[A-Za-z0-9._-]+$/;

const CreateCaregiver = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [locationOptions, setLocationOptions] = useState([]);
    const [sharedUserOptions, setSharedUserOptions] = useState([]);
    const [sharedUsersLoading, setSharedUsersLoading] = useState(false);
    const [showSubordinatePassword, setShowSubordinatePassword] = useState(false);
    const [showSubordinatePasswordConfirm, setShowSubordinatePasswordConfirm] = useState(false);
    const [createdCaregiverRoleUuid, setCreatedCaregiverRoleUuid] = useState('');
    const [createdCaregiverName, setCreatedCaregiverName] = useState('');
    const [caregiverSuccess, setCaregiverSuccess] = useState('');
    const [patientForm, setPatientForm] = useState(getDefaultPatientForm());
    const [patientSubmitting, setPatientSubmitting] = useState(false);
    const [patientError, setPatientError] = useState('');
    const [patientSuccess, setPatientSuccess] = useState('');
    const [form, setForm] = useState({
        name: '',
        email: '',
        nonSharedEmail: '',
        gender: '',
        phone: '',
        age: '',
        birthDate: '',
        street: '',
        zip: '',
        parish: '',
        municipality: '',
        locationUuid: '',
        idCard: '',
        nif: '',
        healthNumber: '',
        niss: '',
        maritalStatus: '',
        education: '',
        professionalStatus: '',
        kinshipToDependent: '',
        livesInIsolation: '',
        referenceContacts: [createEmptyReferenceContact()],
        careDuration: '',
        hasCareSupport: '',
        supportType: '',
        psychosocialNotes: '',
        equipmentItems: [createEmptyEquipmentItem()],
        isSubordinate: false,
        subordinateUsername: '',
        subordinatePassword: '',
        subordinatePasswordConfirm: ''
    });

    const onFieldChange = (field) => (event) => {
        const value =
            event?.target?.type === 'checkbox' ? event.target.checked : event?.target?.value || '';
        setForm((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'email' && !prev.isSubordinate ? { nonSharedEmail: value } : {}),
            ...(field === 'birthDate' ? { age: calculateAgeFromDate(value) } : {})
        }));
    };

    const onReferenceContactChange = (index, field) => (event) => {
        const value = event?.target?.value || '';
        setForm((prev) => ({
            ...prev,
            referenceContacts: prev.referenceContacts.map((contact, contactIndex) =>
                contactIndex === index
                    ? {
                          ...contact,
                          [field]: value
                      }
                    : contact
            )
        }));
    };

    const onAddReferenceContact = () => {
        setForm((prev) => ({
            ...prev,
            referenceContacts: [...(prev.referenceContacts || []), createEmptyReferenceContact()]
        }));
    };

    const onRemoveReferenceContact = (index) => {
        setForm((prev) => {
            const nextContacts = (prev.referenceContacts || []).filter(
                (_, contactIndex) => contactIndex !== index
            );

            return {
                ...prev,
                referenceContacts:
                    nextContacts.length > 0 ? nextContacts : [createEmptyReferenceContact()]
            };
        });
    };

    const onEquipmentItemChange = (index, field) => (event) => {
        const value = event?.target?.value || '';
        setForm((prev) => ({
            ...prev,
            equipmentItems: normalizeEquipmentItems(prev.equipmentItems).map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          [field]: value
                      }
                    : item
            )
        }));
    };

    const onAddEquipmentItem = () => {
        setForm((prev) => ({
            ...prev,
            equipmentItems: [...normalizeEquipmentItems(prev.equipmentItems), createEmptyEquipmentItem()]
        }));
    };

    const onRemoveEquipmentItem = (index) => {
        setForm((prev) => {
            const nextItems = normalizeEquipmentItems(prev.equipmentItems).filter(
                (_, itemIndex) => itemIndex !== index
            );

            return {
                ...prev,
                equipmentItems: nextItems.length > 0 ? nextItems : [createEmptyEquipmentItem()]
            };
        });
    };

    const onSubmit = async () => {
        if (!user.selectedRole) {
            setError('Sem role ativa para executar esta ação.');
            return;
        }

        const normalizedSharedEmails = sharedUserOptions
            .map((item) => String(item?.email || '').trim().toLowerCase())
            .filter(Boolean);

        if (!form.name.trim() || !form.email.trim()) {
            setError('Nome e Email são obrigatórios.');
            return;
        }

        if (form.isSubordinate && !sharedUserOptions.length) {
            setError('Ainda não existem contas partilhadas configuradas.');
            return;
        }

        if (
            !form.isSubordinate &&
            normalizedSharedEmails.includes(form.email.trim().toLowerCase())
        ) {
            setError('Esse email está reservado para contas partilhadas.');
            return;
        }

        if (form.isSubordinate) {
            if (!normalizedSharedEmails.includes(form.email.trim().toLowerCase())) {
                setError('Seleciona um email válido de conta partilhada.');
                return;
            }

            if (!form.subordinateUsername.trim()) {
                setError('O username da conta partilhada e obrigatório.');
                return;
            }

            if (!subordinateUsernamePattern.test(form.subordinateUsername.trim())) {
                setError('O username da conta partilhada so pode ter letras, números, ponto, underscore e hifen.');
                return;
            }

            if (!validatePasswordPolicy(form.subordinatePassword)) {
                setError(passwordPolicyMessage);
                return;
            }

            if (form.subordinatePassword !== form.subordinatePasswordConfirm) {
                setError('As passwords da conta partilhada devem coincidir.');
                return;
            }
        }

        setSubmitting(true);
        setError('');
        setCaregiverSuccess('');
        setPatientError('');
        setPatientSuccess('');

        try {
            const referenceContacts = (form.referenceContacts || [])
                .map((contact) => ({
                    name: contact.name.trim(),
                    kinship: contact.kinship.trim(),
                    age: contact.age.trim(),
                    phone: contact.phone.trim()
                }))
                .filter((contact) => contact.name || contact.kinship || contact.age || contact.phone);

            const equipmentItems = normalizeEquipmentItems(form.equipmentItems)
                .map((item) => ({
                    type: item.type.trim(),
                    details: item.details.trim()
                }))
                .filter((item) => item.type || item.details);

            const firstTabletItem = equipmentItems.find(
                (item) => String(item.type || '').trim().toLowerCase() === 'tablet'
            );

            const response = await createUser({
                userRoleUuid: user.selectedRole,
                payload: {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    role: 'PATIENT_CARE_GIVER',
                    is_subordinate: form.isSubordinate,
                    username: form.isSubordinate ? form.subordinateUsername.trim() : undefined,
                    passwd: form.isSubordinate ? form.subordinatePassword : undefined,
                    gender: form.gender || undefined,
                    phone: form.phone.trim() || undefined,
                    birth_date: toApiDate(form.birthDate),
                    address: {
                        street: form.street.trim() || undefined,
                        zip: form.zip.trim() || undefined
                    },
                    location: form.locationUuid || undefined,
                    user_data: {
                        age: calculateAgeFromDate(form.birthDate) || null,
                        parish: form.parish.trim() || null,
                        municipality: form.municipality.trim() || null,
                        id_card: form.idCard.trim() || null,
                        nif: form.nif.trim() || null,
                        health_number: form.healthNumber.trim() || null,
                        niss: form.niss.trim() || null,
                        marital_status: form.maritalStatus.trim() || null,
                        education: form.education.trim() || null,
                        professional_status: form.professionalStatus.trim() || null,
                        kinship_to_dependent: form.kinshipToDependent.trim() || null,
                        lives_in_isolation: form.livesInIsolation.trim() || null,
                        reference_contacts: referenceContacts,
                        household_member_name: null,
                        household_member_kinship: null,
                        household_member_age: null,
                        household_member_occupation: null,
                        reference_person_name: null,
                        reference_person_kinship: null,
                        reference_person_age: null,
                        reference_person_occupation: null,
                        care_duration: form.careDuration.trim() || null,
                        has_care_support: form.hasCareSupport.trim() || null,
                        support_type: form.supportType.trim() || null,
                        psychosocial_notes: form.psychosocialNotes.trim() || null,
                        equipment_items: equipmentItems,
                        has_tablet_equipment: Boolean(firstTabletItem),
                        tablet_serial_number: firstTabletItem?.details || null,
                        alt_contact_name: null,
                        alt_contact_kinship: null,
                        alt_contact_phone: null,
                        alt_contact_email: null,
                        alt_contact_schedule: null
                    }
                }
            });
            const createdRole = response?.data || {};
            const nextRoleUuid = createdRole?.uuid || createdRole?.role_uuid || '';
            setCreatedCaregiverRoleUuid(nextRoleUuid);
            setCreatedCaregiverName(createdRole?.name || form.name.trim());
            setCaregiverSuccess(
                'Cuidador criado com sucesso. Pode agora adicionar a Pessoa a Cuidados ou concluir mais tarde.'
            );
        } catch (err) {
            setError(err?.response?.data?.description || 'Não foi possível criar cuidador.');
        } finally {
            setSubmitting(false);
        }
    };

    const copyNifToSharedUsername = async () => {
        const nifValue = form.nif.trim();
        if (!nifValue) {
            return;
        }

        setForm((prev) => ({
            ...prev,
            subordinateUsername: nifValue
        }));

        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(nifValue);
            } catch {
                // Best effort only.
            }
        }
    };

    useEffect(() => {
        const loadLocations = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const response = await listLocations({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                const list = Array.isArray(response?.data) ? response.data : [];
                setLocationOptions(list);
            } catch {
                setLocationOptions([]);
            }
        };

        void loadLocations();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadSharedUsers = async () => {
            if (!user.selectedRole) {
                return;
            }

            setSharedUsersLoading(true);

            try {
                const response = await listSharedUsers({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                setSharedUserOptions(Array.isArray(response?.data) ? response.data : []);
            } catch {
                setSharedUserOptions([]);
            } finally {
                setSharedUsersLoading(false);
            }
        };

        void loadSharedUsers();
    }, [user.selectedRole]);

    const onPatientFieldChange = (field) => (event) => {
        const value = event?.target?.value || '';
        setPatientForm((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'birthDate' ? { age: calculateAgeFromDate(value) } : {})
        }));
    };

    const onSubmitPatient = async () => {
        if (!user.selectedRole || !createdCaregiverRoleUuid) {
            setPatientError('Cria primeiro o cuidador antes de adicionares a Pessoa a Cuidados.');
            return;
        }

        if (!patientForm.name.trim()) {
            setPatientError('O nome da Pessoa a Cuidados é obrigatório.');
            return;
        }

        setPatientSubmitting(true);
        setPatientError('');
        setPatientSuccess('');

        try {
            const response = await createPatientAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    caregiver: createdCaregiverRoleUuid,
                    name: patientForm.name.trim(),
                    gender: toApiGender(patientForm.gender),
                    birth_date: toApiDate(patientForm.birthDate),
                    address: {
                        street: patientForm.street.trim() || undefined,
                        zip: patientForm.zip.trim() || undefined
                    },
                    user_data: {
                        age: calculateAgeFromDate(patientForm.birthDate) || null,
                        street: patientForm.street.trim() || null,
                        zip: patientForm.zip.trim() || null,
                        parish: patientForm.parish.trim() || null,
                        municipality: patientForm.municipality.trim() || null,
                        id_card: patientForm.idCard.trim() || null,
                        nif: patientForm.nif.trim() || null,
                        health_number: patientForm.healthNumber.trim() || null,
                        niss: patientForm.niss.trim() || null,
                        marital_status: patientForm.maritalStatus.trim() || null,
                        education: patientForm.education.trim() || null,
                        professional_status: patientForm.professionalStatus.trim() || null,
                        disability: patientForm.disability.trim() || null
                    }
                }
            });

            const created = response?.data || {};
            setPatientForm((prev) => ({
                ...prev,
                uuid: created?.uuid || prev.uuid
            }));
            navigate(`/caregivers/${createdCaregiverRoleUuid}?tab=personal`);
        } catch (err) {
            setPatientError(err?.response?.data?.description || 'Não foi possí­vel criar a Pessoa a Cuidados.');
        } finally {
            setPatientSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#fff' }}>
            <Paper
                variant="outlined"
                sx={{
                    mt: 2,
                    p: { xs: 2, md: 3 },
                    bgcolor: '#fff',
                    borderRadius: 2
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                    <IconButton
                        sx={{
                            width: 40,
                            height: 40,
                            border: '1.5px solid #BDBDBD',
                            bgcolor: '#FFFFFF',
                            color: '#3D3D3D',
                            borderRadius: '12px',
                            '&:hover': {
                                bgcolor: '#F7F7F7'
                            }
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIosNewRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>
                        Criar Novo Cuidador
                    </Typography>
                </Stack>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={4}>
                        <Box>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Ficha Pessoal: Cuidador
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Nome Completo"
                                    fullWidth
                                    value={form.name}
                                    onChange={onFieldChange('name')}
                                />
                                <TextField
                                    label="Género"
                                    fullWidth
                                    select
                                    value={form.gender}
                                    onChange={onFieldChange('gender')}
                                >
                                    <MenuItem value="">Selecionar</MenuItem>
                                    <MenuItem value="WOMAN">Feminino</MenuItem>
                                    <MenuItem value="MAN">Masculino</MenuItem>
                                    <MenuItem value="NOT_LISTED">Outro</MenuItem>
                                </TextField>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <DatePicker
                                    label="Data de Nascimento"
                                    format="DD/MM/YYYY"
                                    value={toPickerValue(form.birthDate)}
                                    onChange={(newValue) =>
                                        onFieldChange('birthDate')({
                                            target: {
                                                value:
                                                    newValue && dayjs(newValue).isValid()
                                                        ? dayjs(newValue).format('YYYY-MM-DD')
                                                        : ''
                                            }
                                        })
                                    }
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                                <TextField
                                    label="Idade"
                                    fullWidth
                                    value={form.age}
                                    disabled
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="Contato Telefónico"
                                    fullWidth
                                    value={form.phone}
                                    onChange={onFieldChange('phone')}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Morada"
                                    fullWidth
                                    value={form.street}
                                    onChange={onFieldChange('street')}
                                />
                                <TextField
                                    label="Código Postal"
                                    fullWidth
                                    value={form.zip}
                                    onChange={onFieldChange('zip')}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Freguesia"
                                    fullWidth
                                    value={form.parish}
                                    onChange={onFieldChange('parish')}
                                />
                                <TextField
                                    label="Concelho"
                                    fullWidth
                                    value={form.municipality}
                                    onChange={onFieldChange('municipality')}
                                />
                                <TextField
                                    label="Localização SOS"
                                    fullWidth
                                    select
                                    value={form.locationUuid}
                                    onChange={onFieldChange('locationUuid')}
                                    helperText="Define a localização cujos contactos SOS ficam associados a este cuidador."
                                >
                                    <MenuItem value="">Sem localização</MenuItem>
                                    {locationOptions.map((option) => (
                                        <MenuItem key={option.uuid} value={option.uuid}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="N.º BI / CC"
                                    fullWidth
                                    value={form.idCard}
                                    onChange={onFieldChange('idCard')}
                                />
                                <TextField
                                    label="NIF"
                                    fullWidth
                                    value={form.nif}
                                    onChange={onFieldChange('nif')}
                                />
                                <TextField
                                    label="N.º Utente"
                                    fullWidth
                                    value={form.healthNumber}
                                    onChange={onFieldChange('healthNumber')}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="NISS"
                                    fullWidth
                                    value={form.niss}
                                    onChange={onFieldChange('niss')}
                                />
                                <Box />
                                <Box />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 3
                                }}
                            >
                                <TextField
                                    label="Estado Civil"
                                    fullWidth
                                    value={form.maritalStatus}
                                    onChange={onFieldChange('maritalStatus')}
                                />
                                <TextField
                                    label="Escolaridade"
                                    fullWidth
                                    value={form.education}
                                    onChange={onFieldChange('education')}
                                />
                                <TextField
                                    label="Situação Profissional"
                                    fullWidth
                                    value={form.professionalStatus}
                                    onChange={onFieldChange('professionalStatus')}
                                />
                            </Box>

                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={2}
                                mb={1}
                            >
                                <Typography variant="h6" fontWeight={700}>
                                    Conta e email
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.isSubordinate}
                                            onChange={(event) => {
                                                const checked = event.target.checked;

                                                if (checked && !sharedUserOptions.length) {
                                                    setError(
                                                        'Ainda não existem contas partilhadas configuradas.'
                                                    );
                                                    return;
                                                }

                                                const firstSharedEmail = sharedUserOptions[0]?.email || '';
                                                setError('');
                                                setForm((prev) => ({
                                                    ...prev,
                                                    isSubordinate: checked,
                                                    email: checked
                                                        ? prev.email && sharedUserOptions.some((item) => item.email === prev.email)
                                                            ? prev.email
                                                            : firstSharedEmail
                                                        : prev.nonSharedEmail,
                                                    subordinateUsername: checked
                                                        ? prev.subordinateUsername
                                                        : '',
                                                    subordinatePassword: checked
                                                        ? prev.subordinatePassword
                                                        : '',
                                                    subordinatePasswordConfirm: checked
                                                        ? prev.subordinatePasswordConfirm
                                                        : ''
                                                }));
                                            }}
                                        />
                                    }
                                    label="Conta partilhada"
                                    sx={{ m: 0 }}
                                />
                            </Stack>

                            <Box sx={{ mb: 2 }}>
                                {form.isSubordinate ? (
                                    <TextField
                                        select
                                        label="E-mail da conta partilhada"
                                        fullWidth
                                        value={form.email}
                                        onChange={onFieldChange('email')}
                                        disabled={sharedUsersLoading || !sharedUserOptions.length}
                                        helperText={
                                            sharedUserOptions.length
                                                ? 'Seleciona qual o email base a usar nesta conta partilhada.'
                                                : 'Não existem contas partilhadas disponíveis. Cria primeiro uma nas Definições.'
                                        }
                                    >
                                        {sharedUserOptions.map((item) => (
                                            <MenuItem key={item.uuid} value={item.email}>
                                                {item.email}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        label="E-mail"
                                        fullWidth
                                        value={form.email}
                                        onChange={onFieldChange('email')}
                                        helperText="Os emails configurados como contas partilhadas não podem ser usados em contas normais."
                                    />
                                )}
                            </Box>

                            {form.isSubordinate ? (
                                <Box
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 2,
                                        border: '1px solid #E2E2E2',
                                        bgcolor: '#FAFAFA'
                                    }}
                                >
                                    <Typography variant="body2" color="#5F5F5F" mb={2}>
                                        Este cuidador utilizará o e-mail partilhado na autenticação principal
                                        e depois entrará com username e password próprios.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                            gap: 2
                                        }}
                                    >
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={form.subordinateUsername}
                                            onChange={onFieldChange('subordinateUsername')}
                                            helperText="Use letras, números, ponto, underscore ou hifen."
                                            autoComplete="new-password"
                                            inputProps={{
                                                autoComplete: 'new-password',
                                                spellCheck: 'false'
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={copyNifToSharedUsername}
                                                            disabled={!form.nif.trim()}
                                                            title="Usar NIF como username"
                                                        >
                                                            <ContentCopyOutlinedIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            label="Password"
                                            type={showSubordinatePassword ? 'text' : 'password'}
                                            fullWidth
                                            value={form.subordinatePassword}
                                            onChange={onFieldChange('subordinatePassword')}
                                            helperText={passwordPolicyMessage}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() =>
                                                                setShowSubordinatePassword((prev) => !prev)
                                                            }
                                                        >
                                                            {showSubordinatePassword ? (
                                                                <VisibilityOffOutlinedIcon />
                                                            ) : (
                                                                <VisibilityOutlinedIcon />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            label="Confirmar Password"
                                            type={showSubordinatePasswordConfirm ? 'text' : 'password'}
                                            fullWidth
                                            value={form.subordinatePasswordConfirm}
                                            onChange={onFieldChange('subordinatePasswordConfirm')}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() =>
                                                                setShowSubordinatePasswordConfirm((prev) => !prev)
                                                            }
                                                        >
                                                            {showSubordinatePasswordConfirm ? (
                                                                <VisibilityOffOutlinedIcon />
                                                            ) : (
                                                                <VisibilityOutlinedIcon />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : null}

                            <Typography variant="h6" fontWeight={700} mb={1}>
                                Situação Familiar
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 2,
                                    mb: 3
                                }}
                            >
                                <TextField
                                    label="Relação de parentesco com a pessoa dependente"
                                    fullWidth
                                    value={form.kinshipToDependent}
                                    onChange={onFieldChange('kinshipToDependent')}
                                />
                                <TextField
                                    label="Vive em situação de isolamento?"
                                    fullWidth
                                    value={form.livesInIsolation}
                                    onChange={onFieldChange('livesInIsolation')}
                                />
                            </Box>

                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                            >
                                <Typography variant="h6" fontWeight={700}>
                                    Outros familiares, vizinhos e/ou outras pessoas de referência
                                </Typography>
                                <IconButton onClick={onAddReferenceContact} sx={{ color: '#D63731' }}>
                                    <AddCircleOutlineRoundedIcon />
                                </IconButton>
                            </Stack>
                            <Stack spacing={2} mb={3}>
                                {(form.referenceContacts || []).map((contact, index) => (
                                    <Box
                                        key={`reference-contact-${index}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                md: '2fr 2fr 0.7fr 2fr auto'
                                            },
                                            gap: 2,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <TextField
                                            label="Nome"
                                            fullWidth
                                            value={contact.name}
                                            onChange={onReferenceContactChange(index, 'name')}
                                        />
                                        <TextField
                                            label="Parentesco"
                                            fullWidth
                                            value={contact.kinship}
                                            onChange={onReferenceContactChange(index, 'kinship')}
                                        />
                                        <TextField
                                            label="Idade"
                                            fullWidth
                                            value={contact.age}
                                            onChange={onReferenceContactChange(index, 'age')}
                                        />
                                        <TextField
                                            label="Telefone"
                                            fullWidth
                                            value={contact.phone}
                                            onChange={onReferenceContactChange(index, 'phone')}
                                        />
                                        <IconButton
                                            onClick={() => onRemoveReferenceContact(index)}
                                            disabled={(form.referenceContacts || []).length === 1}
                                            sx={{ color: '#3D3D3D' }}
                                        >
                                            <RemoveCircleOutlineRoundedIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>

                            <Typography variant="h6" fontWeight={700} mb={1}>
                                Situação Psicossocial
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Há quanto tempo presta cuidados?"
                                    fullWidth
                                    value={form.careDuration}
                                    onChange={onFieldChange('careDuration')}
                                />
                                <TextField
                                    label="Recebe apoio nos cuidados que presta?"
                                    fullWidth
                                    value={form.hasCareSupport}
                                    onChange={onFieldChange('hasCareSupport')}
                                />
                                <TextField
                                    label="Tipo de apoio"
                                    fullWidth
                                    value={form.supportType}
                                    onChange={onFieldChange('supportType')}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    label="Outras informações importantes acerca da situação psicossocial"
                                    fullWidth
                                    value={form.psychosocialNotes}
                                    onChange={onFieldChange('psychosocialNotes')}
                                />
                            </Box>

                        </Box>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={2}
                                mb={1}
                            >
                                <Typography variant="h6" fontWeight={700}>
                                    Equipamentos
                                </Typography>
                                <IconButton onClick={onAddEquipmentItem} sx={{ color: '#3D3D3D' }}>
                                    <AddCircleOutlineRoundedIcon />
                                </IconButton>
                            </Stack>

                            <Stack spacing={2} mb={3}>
                                {normalizeEquipmentItems(form.equipmentItems).map((item, index) => (
                                    <Box
                                        key={`equipment-${index}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: '220px 1fr auto' },
                                            gap: 2,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <TextField
                                            label="Tipo"
                                            fullWidth
                                            select
                                            value={item.type}
                                            onChange={onEquipmentItemChange(index, 'type')}
                                        >
                                            <MenuItem value="Tablet">Tablet</MenuItem>
                                            <MenuItem value="Wearable">Wearable</MenuItem>
                                            <MenuItem value="Outro">Outro</MenuItem>
                                        </TextField>
                                        <TextField
                                            label="Nº de Identificação / Observações"
                                            fullWidth
                                            value={item.details}
                                            onChange={onEquipmentItemChange(index, 'details')}
                                        />
                                        <IconButton
                                            onClick={() => onRemoveEquipmentItem(index)}
                                            disabled={normalizeEquipmentItems(form.equipmentItems).length === 1}
                                            sx={{ color: '#3D3D3D' }}
                                        >
                                            <RemoveCircleOutlineRoundedIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>

                        {caregiverSuccess ? (
                            <Alert severity="success" sx={{ borderRadius: '12px' }}>
                                {caregiverSuccess}
                            </Alert>
                        ) : null}

                        {createdCaregiverRoleUuid ? (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: { xs: 2, md: 3 },
                                    border: '1px solid #E2E2E2',
                                    borderRadius: 2,
                                    bgcolor: '#FAFAFA'
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                    spacing={1.5}
                                    mb={3}
                                >
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            Ficha Pessoal: Pessoa a Cuidados
                                        </Typography>
                                        <Typography variant="body2" color="#6F6F6F">
                                            Passo opcional para associar já o paciente ao cuidador {createdCaregiverName || form.name}.
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        sx={secondaryButtonSx}
                                        onClick={() => navigate(`/caregivers/${createdCaregiverRoleUuid}?tab=personal`)}
                                    >
                                        Concluir mais tarde
                                    </Button>
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="Nome Completo"
                                        fullWidth
                                        value={patientForm.name}
                                        onChange={onPatientFieldChange('name')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="Género"
                                        fullWidth
                                        select
                                        value={patientForm.gender}
                                        onChange={onPatientFieldChange('gender')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    >
                                        <MenuItem value="">Selecionar</MenuItem>
                                        <MenuItem value="WOMAN">Feminino</MenuItem>
                                        <MenuItem value="MAN">Masculino</MenuItem>
                                        <MenuItem value="NOT_LISTED">Outro</MenuItem>
                                    </TextField>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <DatePicker
                                        label="Data de Nascimento"
                                        format="DD/MM/YYYY"
                                        value={toPickerValue(patientForm.birthDate)}
                                        onChange={(newValue) =>
                                            onPatientFieldChange('birthDate')({
                                                target: {
                                                    value:
                                                        newValue && dayjs(newValue).isValid()
                                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                                            : ''
                                                }
                                            })
                                        }
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                    <TextField
                                        label="Idade"
                                        fullWidth
                                        value={patientForm.age}
                                        disabled
                                        InputProps={{ readOnly: true }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="Morada"
                                        fullWidth
                                        value={patientForm.street}
                                        onChange={onPatientFieldChange('street')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="Código Postal"
                                        fullWidth
                                        value={patientForm.zip}
                                        onChange={onPatientFieldChange('zip')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="Freguesia"
                                        fullWidth
                                        value={patientForm.parish}
                                        onChange={onPatientFieldChange('parish')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="Concelho"
                                        fullWidth
                                        value={patientForm.municipality}
                                        onChange={onPatientFieldChange('municipality')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="N.º BI / CC"
                                        fullWidth
                                        value={patientForm.idCard}
                                        onChange={onPatientFieldChange('idCard')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="NIF"
                                        fullWidth
                                        value={patientForm.nif}
                                        onChange={onPatientFieldChange('nif')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="N.º Utente"
                                        fullWidth
                                        value={patientForm.healthNumber}
                                        onChange={onPatientFieldChange('healthNumber')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="NISS"
                                        fullWidth
                                        value={patientForm.niss}
                                        onChange={onPatientFieldChange('niss')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <Box />
                                    <Box />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <TextField
                                        label="Estado Civil"
                                        fullWidth
                                        value={patientForm.maritalStatus}
                                        onChange={onPatientFieldChange('maritalStatus')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="Escolaridade"
                                        fullWidth
                                        value={patientForm.education}
                                        onChange={onPatientFieldChange('education')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                    <TextField
                                        label="Situação Profissional"
                                        fullWidth
                                        value={patientForm.professionalStatus}
                                        onChange={onPatientFieldChange('professionalStatus')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        label="Incapacidade"
                                        fullWidth
                                        value={patientForm.disability}
                                        onChange={onPatientFieldChange('disability')}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    />
                                </Box>

                                {patientError ? (
                                    <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>
                                        {patientError}
                                    </Alert>
                                ) : null}

                                {patientSuccess ? (
                                    <Alert severity="success" sx={{ borderRadius: '12px', mb: 2 }}>
                                        {patientSuccess}
                                    </Alert>
                                ) : null}

                                <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                                    <Button
                                        variant="outlined"
                                        sx={secondaryButtonSx}
                                        onClick={() => navigate(`/caregivers/${createdCaregiverRoleUuid}?tab=personal`)}
                                    >
                                        Abrir cuidador
                                    </Button>
                                    <Button
                                        variant="contained"
                                        sx={primaryButtonSx}
                                        onClick={onSubmitPatient}
                                        disabled={patientSubmitting || Boolean(patientSuccess)}
                                    >
                                        {patientSubmitting ? 'A criar...' : 'Criar paciente'}
                                    </Button>
                                </Stack>
                            </Box>
                        ) : null}

                        {error ? (
                            <Alert severity="error" sx={{ borderRadius: '12px' }}>
                                {error}
                            </Alert>
                        ) : null}

                        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                            <Button
                                variant="outlined"
                                sx={secondaryButtonSx}
                                onClick={() => navigate(-1)}
                                disabled={submitting || patientSubmitting}
                            >
                                {createdCaregiverRoleUuid ? 'Voltar à lista' : 'Cancelar'}
                            </Button>
                            <Button
                                variant="contained"
                                sx={primaryButtonSx}
                                onClick={onSubmit}
                                disabled={submitting || Boolean(createdCaregiverRoleUuid)}
                            >
                                {submitting
                                    ? 'A criar...'
                                    : createdCaregiverRoleUuid
                                        ? 'Cuidador criado'
                                        : 'Criar cuidador'}
                            </Button>
                        </Stack>
                    </Stack>
                </LocalizationProvider>
            </Paper>
        </Box>
    );
};

export default CreateCaregiver;
